// Cloudflare Workers API クライアント
import { supabase } from './supabase'

const WORKER_URL = import.meta.env.VITE_WORKER_URL as string

async function workerFetch(path: string, options?: RequestInit): Promise<Response> {
  // ログインユーザーのアクセストークンを付与（Worker 側で本人確認）
  const { data: { session } } = await supabase.auth.getSession()
  const authHeader: Record<string, string> = session?.access_token
    ? { Authorization: `Bearer ${session.access_token}` }
    : {}
  const res = await fetch(`${WORKER_URL}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...authHeader, ...options?.headers },
  })
  if (!res.ok) throw new Error(`Worker API error: ${res.status} ${res.statusText}`)
  return res
}

export async function fetchItems(params?: { cat?: string; status?: string; limit?: number }) {
  const query = new URLSearchParams()
  if (params?.cat) query.set('cat', params.cat)
  if (params?.status) query.set('status', params.status)
  if (params?.limit) query.set('limit', String(params.limit))
  const res = await workerFetch(`/api/items?${query}`)
  return res.json() as Promise<{ items: MemoItem[] }>
}

export async function reprocessItem(item_id: string, mode: string): Promise<void> {
  await workerFetch('/api/reprocess', {
    method: 'POST',
    body: JSON.stringify({ item_id, mode }),
  })
}

export async function checkHealth() {
  const res = await workerFetch('/health')
  return res.json() as Promise<{ ok: boolean; env: string }>
}

// 💬チャットのドロップダウンの選択肢（value＝Worker に送る選択キー、label＝表示名）。
// 一覧はここに書かず、Worker（models.ts）から取得する。並び＝表示順、先頭＝既定の選択肢。
// 画面からは hooks.ts の useChatModels() を使う。
export type ChatModelOption = { value: string; label: string }

// models … Gemini（/api/item-chat）。メモ詳細と /chat の両方に出す。
// claude … Claude（/api/claude-chat）。/chat だけに出す。メモ参照なしの素のチャット専用のため、
//          メモ詳細や Ask the Lake には出さない。Worker に API キーが未設定の間は空で返る。
export type ChatModelList = { models: ChatModelOption[]; claude: ChatModelOption[] }

export async function fetchChatModels(): Promise<ChatModelList> {
  const res = await workerFetch('/api/chat-models')
  const data = await res.json() as Partial<ChatModelList>
  if (!Array.isArray(data.models) || data.models.length === 0) throw new Error('chat-models: 一覧が空です')
  return { models: data.models, claude: Array.isArray(data.claude) ? data.claude : [] }
}

// 保存済みメッセージの model 列を表示名に変換する（過去の行の表示用）。
// 過去の行にはキー（flash など）が保存されている。これは当時の表示名のまま固定する
// （ドロップダウンの文言を変えても、過去の回答の表示は変えない）。
// 新しい行には Worker が返した表示名（例: 'Flash Lite 3.5'）が保存されるので、表に無ければそのまま表示する。
const SAVED_KEY_LABEL: Record<string, string> = {
  flash:   'Flash',
  flash38: 'Flash 3.8',
  pro25:   'Pro 2.5',
  pro31:   'Pro 3.1',
  sonnet5: 'Sonnet 5',
}

export function modelLabel(model: string | null | undefined): string {
  if (!model) return ''
  return SAVED_KEY_LABEL[model] ?? model
}

// チャット画面の Claude モデル。メモは参照しない（履歴＋質問のみ）。
// ウェブ検索は Worker 側で常時有効（web_search / 最大6回）。
// 引数の model は選択キー。戻りの model は Worker が実際に使ったモデルの表示名（保存・表示にそのまま使う）。
export async function claudeChat(
  query: string,
  history: { role: 'user' | 'assistant'; content: string }[] = [],
  model?: string,
): Promise<{ answer: string; searched?: boolean; model?: string }> {
  const res = await workerFetch('/api/claude-chat', {
    method: 'POST',
    body: JSON.stringify({ query, history, model }),
  })
  return res.json() as Promise<{ answer: string; searched?: boolean; model?: string }>
}

// アイテム個別の Gemini チャット（モデル選択可）。
// 戻りの model は Worker が実際に使ったモデルの表示名（例: 'Flash Lite 3.5'）。保存・表示にそのまま使う。
// 引数の model（選択キー）を省くと、Worker が既定のモデルで回答する。
export async function itemChat(
  item_id: string,
  query: string,
  history: { role: 'user' | 'assistant'; content: string }[] = [],
  model?: string,
  includeMemos = false,
): Promise<{ answer: string; searched?: boolean; attempt?: number; model?: string }> {
  const res = await workerFetch('/api/item-chat', {
    method: 'POST',
    body: JSON.stringify({ item_id, query, history, model, includeMemos }),
  })
  return res.json() as Promise<{ answer: string; searched?: boolean; attempt?: number; model?: string }>
}

// 新規チャット用の kind='chat' メモを作成し item_id を返す
export async function createChatMemo(title: string): Promise<{ item_id: string }> {
  const res = await workerFetch('/api/chat-memo', {
    method: 'POST',
    body: JSON.stringify({ title }),
  })
  return res.json() as Promise<{ item_id: string }>
}

// Gemini会話セッションをメモとして保存（手動・カテゴリ自動分類）
export async function chatToMemo(session_id: string): Promise<{ ok: boolean; item_id?: string; category?: string; summary?: string }> {
  const res = await workerFetch('/api/chat-to-memo', {
    method: 'POST',
    body: JSON.stringify({ session_id }),
  })
  return res.json() as Promise<{ ok: boolean; item_id?: string; category?: string; summary?: string }>
}

// /chat ページのチャット（ml_item_chats）をメモとして保存
export async function itemChatToMemo(item_id: string): Promise<{ ok: boolean; item_id?: string; category?: string; summary?: string }> {
  const res = await workerFetch('/api/chat-to-memo', {
    method: 'POST',
    body: JSON.stringify({ item_id }),
  })
  return res.json() as Promise<{ ok: boolean; item_id?: string; category?: string; summary?: string }>
}

// 全体チャット（文脈付き・メモ参照切替）
export async function askLake(
  query: string,
  opts: { history?: { role: 'user' | 'assistant'; content: string }[]; useMemos?: boolean } = {},
): Promise<{ answer: string; sources?: { item_id: string; title: string }[]; actions?: string[]; model?: string }> {
  const res = await workerFetch('/api/chat', {
    method: 'POST',
    body: JSON.stringify({ query, history: opts.history ?? [], useMemos: opts.useMemos ?? true }),
  })
  return res.json() as Promise<{ answer: string; sources?: { item_id: string; title: string }[]; actions?: string[]; model?: string }>
}

// 会話スレッドのタイトルを Gemini に生成させる
export async function chatTitle(text: string): Promise<{ title: string }> {
  const res = await workerFetch('/api/chat-title', {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
  return res.json() as Promise<{ title: string }>
}

// ── ニュースソース管理 ────────────────────────────────────

export interface NewsSource {
  id: string
  site_name: string | null
  top_url: string
  fetch_type: 'rss' | 'tavily'
  feed_url: string | null
  is_active: boolean
  sort_order: number | null
  created_at: string
}

export async function fetchNewsSources(): Promise<{ sources: NewsSource[] }> {
  const res = await workerFetch('/api/news-sources')
  return res.json() as Promise<{ sources: NewsSource[] }>
}

export async function addNewsSource(top_url: string, site_name?: string): Promise<{ source: NewsSource }> {
  const res = await workerFetch('/api/news-sources', {
    method: 'POST',
    body: JSON.stringify({ top_url, site_name }),
  })
  return res.json() as Promise<{ source: NewsSource }>
}

export async function updateNewsSource(id: string, patch: { is_active?: boolean; site_name?: string; sort_order?: number }): Promise<void> {
  await workerFetch('/api/news-sources', {
    method: 'PATCH',
    body: JSON.stringify({ id, ...patch }),
  })
}

export async function deleteNewsSource(id: string): Promise<void> {
  await workerFetch('/api/news-sources', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  })
}

// ── 配信ニュース一覧アクション ────────────────────────────
// 📥 keep: 記事を MemoLake 本体に保存（興味あり）
export async function newsKeep(article_id: string): Promise<{ ok: boolean; item_id: string | null }> {
  const res = await workerFetch('/api/news-keep', { method: 'POST', body: JSON.stringify({ article_id }) })
  return res.json() as Promise<{ ok: boolean; item_id: string | null }>
}

// ⭐ お気に入りトグル: Discord の fav ボタンと同一処理を Worker 側で実行する。
// supabase を直接更新してはいけない（ロッキオ学習とメモ同期が飛ぶ）。
export async function newsFavorite(article_id: string): Promise<{ ok: boolean; is_favorite: boolean }> {
  const res = await workerFetch('/api/news-favorite', { method: 'POST', body: JSON.stringify({ article_id }) })
  return res.json() as Promise<{ ok: boolean; is_favorite: boolean }>
}

// 📖 深掘り（B）: gemini_note に保存（Discord不要）
//   mode 省略/'body': 本文取得＋解析（失敗時 reason='no_body'）
//   mode='research'  : 手動フォールバック（要約＋Google検索）
export async function newsDeepDive(article_id: string, mode?: 'body' | 'research'): Promise<{ ok: boolean; note?: string; reason?: string; cause?: string; status?: number | null; item_id?: string | null }> {
  const res = await workerFetch('/api/news-deepdive', { method: 'POST', body: JSON.stringify({ article_id, mode }) })
  return res.json() as Promise<{ ok: boolean; note?: string; reason?: string; cause?: string; status?: number | null; item_id?: string | null }>
}

// 📖 メモ詳細ページからの深掘り（item_id 基準）。結果は gemini_note に保存され詳細にインライン表示される。
//   mode 省略/'body': source_url から本文取得＋解析（失敗時 reason='no_body'/'no_source'）
//   mode='research'  : 要約＋Google検索のフォールバック
export async function itemDeepDive(item_id: string, mode?: 'body' | 'research'): Promise<{ ok: boolean; note?: string; searched?: boolean; reason?: string; cause?: string; status?: number | null }> {
  const res = await workerFetch('/api/item-deepdive', { method: 'POST', body: JSON.stringify({ item_id, mode }) })
  return res.json() as Promise<{ ok: boolean; note?: string; searched?: boolean; reason?: string; cause?: string; status?: number | null }>
}

// 今すぐニュース配信を実行（手動トリガー）
export async function runNewsNow(): Promise<{ ok: boolean; triggered?: string }> {
  const res = await workerFetch('/api/run-news?what=news', { method: 'POST' })
  return res.json() as Promise<{ ok: boolean; triggered?: string }>
}

// ソースのテスト取得（このソースから今取れる記事をプレビュー）
export async function testNewsSource(id: string): Promise<{ items: { title: string; url: string; published_at: string | null }[]; fetch_type?: string }> {
  const res = await workerFetch('/api/news-source-test', { method: 'POST', body: JSON.stringify({ id }) })
  return res.json() as Promise<{ items: { title: string; url: string; published_at: string | null }[]; fetch_type?: string }>
}

export interface MemoItem {
  id: string
  kind: 'link' | 'note' | 'image' | 'chat'
  cat_id: string | null
  status: string
  title: string | null
  summary: string | null
  digest: string | null
  original_content: string
  gemini_note: string | null
  gemini_note_searched: boolean | null
  rewritten_text: string | null
  custom_prompt: string | null
  process_mode: string
  confidence: number | null
  source_url: string | null
  author: string | null
  created_at: string
  discord_msg_id: string | null
  og_image_url: string | null
  image_url: string | null
  rating: number | null
  is_favorite: boolean | null
}
